import React from 'react'

import { hasDefinedValues, OptionProps } from '@avantstay/backoffice-core'
import { SortDirection } from '@avantstay/data-grid'

import { AddressRadiusProps } from '@new/__global/components/Filters/Filters.types'
import { FilterConfig, SortOrderKey } from '@new/__global/components/FiltersFramework/_types'
import { FilterRenderer } from '@new/__global/components/FiltersFramework/FilterRenderer'
import { FilterStore } from '@new/__global/components/FiltersFramework/FilterStore'

export const EXTENDED_COLUMNS_KEY = 'extendedColumnsView'

export const arrayParamsFormatterAndParser = {
  parser: (value: string) => value.split(','),
  formatter: (values?: string[] | null) => (values ? values.join(',') : ''),
}

export const booleanParamParser = {
  parser: (value: string): boolean => String(value).toLowerCase() === 'true',
}

export const integerParamParser = {
  parser: (value: string): number => parseInt(String(value), 10),
}

export const stringParamParser = {
  parser: (value: string): string => value,
}

export const jsonParamsFormatterAndParser = {
  parser: (value: string): string => JSON.parse(value),
  formatter: (values?: object) => JSON.stringify(values),
}

export const rangeParamParser = {
  parser: (value: string) => {
    const [from, to] = value.split('-')
    return { from: parseInt(from, 10), to: parseInt(to, 10) }
  },
  formatter: (value?: { from?: string; to?: string }) =>
    [value?.from, value?.to].filter(Boolean).join('-'),
}

export const rangeFloatWithNegativeValuesParamParser = {
  parser: (value: string) => {
    const [from, to] = value.split('_')
    return { from: Number(from), to: Number(to) }
  },
  formatter: (value?: { from?: string; to?: string }) =>
    [value?.from, value?.to].filter(value => value !== null).join('_'),
}

export const rangeStringParamParser = {
  parser: (value: string) => {
    const [from, to] = value.split('-')
    return { from, to }
  },
  formatter: (value?: { from?: string; to?: string } | null) =>
    [value?.from, value?.to].filter(Boolean).join('-'),
}

export const addressParamParser = {
  parser: (value: string) => {
    const [address, lat, lgn, radius] = value.split('_')
    return { address, latitude: Number(lat), longitude: Number(lgn), radius: Number(radius) }
  },
  formatter: (value?: AddressRadiusProps) => {
    return [value?.address, value?.latitude, value?.longitude, value?.radius]
      .filter(option => option !== undefined)
      .join('_')
  },
}

export const priceRangeParamParser = {
  parser: (value: string) => {
    const [from, to] = value.split('-')
    return { minPrice: parseInt(from, 10), maxPrice: parseInt(to, 10) }
  },
  formatter: (value?: { minPrice?: number; maxPrice?: number }) =>
    [value?.minPrice?.toString(), value?.maxPrice?.toString()].filter(Boolean).join('-'),
}

export const formatPriceRangePreview = (value?: { minPrice?: number; maxPrice?: number }) =>
  [
    value?.minPrice ? `from-${value.minPrice}` : '',
    value?.maxPrice ? `to-${value.maxPrice}` : '',
  ].filter(Boolean)

export const formatQuantityRangePreview = (value?: { from?: number; to?: number }) =>
  [value?.from ? `min-${value.from}` : '', value?.to ? `max-${value.to}` : ''].filter(Boolean)

export const formatAddressPreview = (value?: AddressRadiusProps) => {
  return [
    value?.latitude && value?.longitude ? `${value.address}` : '',
    value?.radius !== undefined ? `${value.radius} miles` : '',
  ].filter(Boolean)
}

export const enumOptionParamParser = {
  parse: (value: string): string => value?.toUpperCase(),
  format: (value: string): string => value?.toLowerCase(),
}

export function convertToFilterRenderer<ValueType = string>(
  { id, label, component, customComponentProps, filterAsColumnKey }: FilterConfig<ValueType>,
  filterStore: FilterStore,
  setFiltersCache?: React.Dispatch<React.SetStateAction<{ [id: string]: unknown }>>,
  additionalComponentProps?: object,
) {
  if (!component) {
    throw Error(`Filter "${id}" is missing component prop`)
  }
  return (
    <FilterRenderer
      key={id}
      id={id}
      label={label || ''}
      filterStore={filterStore}
      filterComponent={component}
      filterAsColumnKey={filterAsColumnKey}
      onChange={
        setFiltersCache
          ? value => setFiltersCache(currentCache => ({ ...currentCache, [id]: value }))
          : undefined
      }
      customComponentProps={{ ...customComponentProps, ...additionalComponentProps }}
    />
  )
}

export const getFilterComponent = <ValueType = string,>(
  filtersConfig: FilterConfig<ValueType>[],
  filtersStore: FilterStore,
  filterId: string,
) => {
  const filterConfig = filtersConfig.find(filter => filter.id === filterId)
  return filterConfig ? convertToFilterRenderer(filterConfig, filtersStore, undefined) : null
}

export const optionsFilterPreviewFormatter = (
  getOptions: () => OptionProps<string | undefined>[],
) => {
  return {
    filterPreviewNoCapitalization: true,
    filterPreviewFormatter: (values?: string[]) => {
      const options = getOptions()
      if (!options) {
        return values?.length
      }
      return values?.map(value => options.find(option => option.value === value)?.label)
    },
  }
}

export const sortOrderMappings = {
  [SortDirection.None]: undefined,
  [SortDirection.Asc]: SortOrderKey.ascending,
  [SortDirection.Desc]: SortOrderKey.descending,
}

type ValueFormatter = (value?: number) => string
const getFormattedValue = (value?: number, formatter?: ValueFormatter) =>
  formatter ? formatter(value) : value

export const formatMinMaxFilterPreview = (
  value: { min?: number; max?: number; exact?: number },
  formatter?: ValueFormatter,
) => {
  if (!value) {
    return undefined
  }
  if (!value || !hasDefinedValues(value)) {
    return undefined
  }
  if (value.exact !== undefined || value.min === value.max) {
    return `= ${getFormattedValue(value.exact !== undefined ? value.exact : value.min, formatter)}`
  }

  if (value.min === undefined) {
    return `< ${getFormattedValue(value.max, formatter)}`
  }

  if (value.max === undefined) {
    return `> ${getFormattedValue(value.min, formatter)}`
  }

  return `${getFormattedValue(value.min, formatter)} - ${getFormattedValue(value.max, formatter)}`
}
